import { ContentState, EditorState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';

type CustomChunkGenerator = Parameters<typeof htmlToDraft>[1];

/**
 * Safely build a Draft.js ContentState from an HTML string.
 *
 * `htmlToDraft` can return an object whose `contentBlocks` is null/undefined for
 * empty or malformed HTML (and for some embedded media). Passing that straight
 * into `ContentState.createFromBlockArray` makes Immutable.js iterate over
 * `undefined` and throw `Cannot read properties of undefined (reading 'set')`,
 * which white-screens the whole page. This guards every such call site.
 */
export function safeContentStateFromHtml(html: string | null | undefined, customChunkGenerator?: CustomChunkGenerator): ContentState {
  const result = htmlToDraft(html ?? '', customChunkGenerator);
  const contentBlocks = result?.contentBlocks;
  if (!Array.isArray(contentBlocks) || contentBlocks.length === 0) {
    return ContentState.createFromText('');
  }
  return ContentState.createFromBlockArray(contentBlocks, result.entityMap);
}

/** Convenience wrapper returning an EditorState ready to render. */
export function safeEditorStateFromHtml(html: string | null | undefined, customChunkGenerator?: CustomChunkGenerator): EditorState {
  return EditorState.createWithContent(safeContentStateFromHtml(html, customChunkGenerator));
}
